import React, { useCallback, useEffect, useRef, useState } from "react";
import LoadingState from "../../Components/LoadingState";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RoutesPaths } from "../../Router/constant";
import {
  getAssistantById,
  listAssistants,
  setActiveAssistant,
} from "../../actions/assistants";
import Stepper from "./onboarding/Stepper";
import StepWebsite, { PENDING_STATUSES } from "./onboarding/StepWebsite";
import StepReview from "./onboarding/StepReview";
import StepInstall from "./onboarding/StepInstall";
import { hostOf } from "../../utils/url";

// The same cadence the first step polls at while it is on screen.
const POLL_INTERVAL_MS = 5000;

/**
 * The three steps that build an assistant, on a route of their own.
 *
 * Reached two ways, and they are the same thing: a new account with nothing
 * yet, sent here by the session check, and an existing account adding another
 * from the sidebar. Needs a session either way - the steps create records -
 * so it is not a public route.
 */
export default function CreateAssistant() {
  const [step, setStep] = useState(1);
  // Set once POST /assistants succeeds; the later steps load the record with it.
  const [assistantId, setAssistantId] = useState("");
  // An assistant from a previous visit whose crawl never finished - the window
  // was closed, or the network went. The record is still there and the server
  // is still reading the site, so this picks it up rather than starting again.
  const [resumed, setResumed] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // POST /assistants points the app at what it created, so the id survives a
  // reload even though this component's state does not.
  const storedId = useSelector((state) => state.assistants.activeId);

  // Login passes a fresh arrow every render; held in a ref so the poll below
  // is not torn down and restarted on each one.
  const onReady = useRef(null);
  onReady.current = (assistant) => {
    setResumed(null);
    setAssistantId(assistant.id);
    // Read and ready: the next thing to do is review it.
    setStep(2);
  };

  // Asks what the stored assistant is doing. It only reports; moving the
  // page on is the poll's business, because an assistant that was *already*
  // ready when this page opened is not one being built now - an account
  // adding its second assistant has one of those, and jumping it into step 2
  // would hand it the old assistant instead of a new one.
  const check = useCallback(
    async (id) => {
      const result = await dispatch(getAssistantById(id));

      if (!result?.success) return "gone";

      const assistant = result.assistant;
      const status = assistant?.status;

      if (PENDING_STATUSES.includes(status)) {
        setResumed(assistant);
        return "pending";
      }

      return status === "ready" ? "ready" : "stopped";
    },
    [dispatch],
  );

  // On arrival: is there a crawl still running from last time?
  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      if (!storedId) {
        setChecking(false);
        return;
      }

      const state = await check(storedId);
      if (cancelled) return;

      setChecking(false);
      if (state !== "pending") return;
    };

    start();

    return () => {
      cancelled = true;
    };
    // Deliberately on mount only: a crawl that finishes is handled by the poll
    // below, and re-running this on every id change would fight with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep asking while it is still reading. Chained rather than an interval, so
  // a slow answer cannot stack requests on top of each other.
  // Keyed on the id, not the record: each poll stores a fresh object, and
  // depending on it would tear the timer down and start it again every time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resumedId = resumed?.id;

  useEffect(() => {
    if (!resumedId) return undefined;

    let cancelled = false;
    let timer = null;

    const tick = async () => {
      const state = await check(resumedId);
      if (cancelled) return;

      if (state === "pending") {
        timer = setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }

      // Done reading: on to the review step with it. Anything else - a failed
      // crawl, a record that has gone - drops the notice and leaves the form
      // below, which is where starting again belongs.
      if (state === "ready") onReady.current({ id: resumedId });
      else setResumed(null);
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [resumedId, check]);

  const host = hostOf(resumed?.sourceUrl);

  // Nothing is drawn until the check is done: step 1 flashing up and then
  // being replaced reads as the form having been lost.
  if (checking) {
    return (
      <main className="onboarding">
        <div className="app-boot">
          <LoadingState
            title="Picking up where you left off"
            detail="Checking whether you've already started an assistant."
          />
        </div>
      </main>
    );
  }

  return (
    <main className="onboarding">
      <div className="onboarding__container">
        {/* A crawl from a previous visit, still going. Said at the top so it
            is read before the form below it, which would otherwise look like
            the only thing on the page. */}
        {resumed && (
          <div className="onboarding__resume" role="status">
            <span className="onboarding__check-spinner" aria-hidden="true" />
            <div>
              <strong>
                We&apos;re still reading {host || "your website"}
              </strong>
              <span>
                Your assistant is being built from the site you entered
                earlier. This page moves on by itself as soon as it is done —
                you can leave it open.
              </span>
            </div>
          </div>
        )}

        <Stepper currentStep={step} onStepClick={setStep} />
        <p className="onboarding__step-label">Step {step} of 3</p>

        {step === 1 && (
          <StepWebsite
            onContinue={(assistant) => {
              setAssistantId(assistant?.id || "");
              setStep(2);
            }}
          />
        )}
        {step === 2 && (
          <StepReview
            assistantId={assistantId}
            onBack={() => setStep(1)}
            onContinue={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <StepInstall
            assistantId={assistantId}
            onGoLive={() => {
              // The dashboard reads whichever assistant the store points at,
              // and the switcher's list was fetched before this one existed.
              // Without both, the page opens on the previous assistant - or on
              // none at all - until the user reloads and the list comes back.
              if (assistantId) {
                dispatch(setActiveAssistant(assistantId));
                dispatch(listAssistants());
              }

              navigate(RoutesPaths.DASHBOARD);
            }}
          />
        )}
      </div>
    </main>
  );
}
